import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { FaSearch, FaCoins, FaDatabase, FaBolt, FaHdd, FaServer, FaDollarSign, FaClock, FaCubes } from 'react-icons/fa';
import './Pool.css';

const Pool = () => {
  const { pool_name } = useParams();
  const [poolData, setPoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const imageUrl = `/img/pools/${pool_name.toLowerCase()}.webp`;

  useEffect(() => {
    const fetchPoolData = async () => {
      try {
        const response = await axios.get(`/api/pool/${pool_name}`);
        setPoolData(response.data);
        setLoading(false);
      } catch (err) {
        setError('Error fetching pool data');
        setLoading(false);
      }
    };

    fetchPoolData();
  }, [pool_name]);

  const handleImageError = (e) => {
    e.target.src = '/img/pools/default.webp';
  };

  const handleCoinImageError = (e) => {
    e.target.src = '/img/coins/DEFAULT.webp';
  };

  const formatHashrate = (hashrate) => {
    if (hashrate >= 1e12) return (hashrate / 1e12).toFixed(2) + ' TH/s';
    if (hashrate >= 1e9) return (hashrate / 1e9).toFixed(2) + ' GH/s';
    if (hashrate >= 1e6) return (hashrate / 1e6).toFixed(2) + ' MH/s';
    if (hashrate >= 1e3) return (hashrate / 1e3).toFixed(2) + ' kH/s';
    return hashrate + ' H/s';
  };

    const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const filteredCurrencies = poolData?.currencies.filter(currency =>
    currency.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.algo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      name: "Coin",
      cell: row => (
        <div className="coin-cell">
          <img src={`/img/coins/${row.symbol.toUpperCase()}.webp`} alt={row.symbol} className="coin-logo" onError={handleCoinImageError} />
          <div>{row.symbol}</div>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: <><FaCoins /> Name</>,
      selector: row => row.name,
      sortable: true,
    },
    {
      name: <><FaCubes /> Algorithm</>,
      selector: row => row.algo,
      sortable: true,
    },
    {
      name: <><FaServer /> Port/SSL</>,
      selector: row => `${row.port}/${row.ssl_port}`,
      sortable: true,
    },
    {
      name: <><FaDollarSign /> Fees/Solo</>,
      selector: row => `${row.fees}/${row.fees_solo}`,
    },
    {
      name: <><FaClock /> 24h Blocks</>,
      selector: row => row['24h_blocks'],
    },
    {
      name: <><FaDollarSign /> 24h BTC</>,
      selector: row => row['24h_btc'],
    },
    {
      name: <><FaCoins /> Reward</>,
      selector: row => row.reward,
    },
    {
      name: <><FaDollarSign /> Min Payout</>,
      selector: row => row.minimum_payout,
    },
    {
      name: <><FaBolt /> Hashrate</>,
      selector: row => formatHashrate(row.hashrate),
    },
    {
      name: <><FaBolt /> Network Hashrate</>,
      selector: row => formatHashrate(row.network_hashrate),
    },
    {
      name: <><FaHdd /> Workers</>,
      selector: row => `${row.workers_shared}/${row.workers_solo}`,
    },
    {
      name: <><FaCubes /> Height</>,
      selector: row => row.height,
    },
    {
      name: "Last Block",
      selector: row => row.lastblock,
    },
    {
      name: <><FaClock /> Time Since Last Block</>,
      selector: row => formatTime(row.timesincelast),
    },
  ];
  

  if (loading) {
    return <div className="pool-loading">Loading...</div>;
  }

  if (error) {
    return <div className="pool-error">{error}</div>;
  }

  return (
    <div className="pool-container">
      {poolData && (
        <div>
          <div className="pool-header">
            <h2><FaDatabase /> Pool Data: {poolData.pool.name}</h2>
            <img 
              src={imageUrl} 
              alt={`Logo of ${poolData.pool.name}`} 
              onError={handleImageError} 
              className="pool-logo"
            />
            <p>Pool URL: <a href={poolData.pool.url_pool}>{poolData.pool.url_pool}</a></p>
            <p>API URL: <a href={poolData.pool.url_api}>{poolData.pool.url_api}</a></p>
          </div>

          <div className="pool-search-container">
            <FaSearch className="pool-search-icon" />
            <input 
              type="text"
              placeholder="Search by symbol, name, or algorithm..."
              className="pool-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <h3><FaCoins /> Available Coins:</h3>
          <DataTable
            columns={columns}
            data={filteredCurrencies}
            pagination
            responsive
            highlightOnHover
            defaultSortField="symbol"
          />
        </div>
      )}
    </div>
  );
};

export default Pool;
